import React, { useState, useEffect } from 'react';
import { FaCheck } from 'react-icons/fa';

const VariantSelector = ({ product, onVariantChange, selectedVariant, currencySymbol = '₹' }) => {
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [availableVariants, setAvailableVariants] = useState([]);
  const [currentVariant, setCurrentVariant] = useState(null);

  // Initialize selected attributes from product attributes
  useEffect(() => {
    if (product?.attributes && product.attributes.length > 0) {
      const initial = {};
      product.attributes.forEach(attr => {
        if (attr.selectedValue) {
          initial[attr.attributeName] = attr.selectedValue;
        }
      });
      setSelectedAttributes(initial);
    }
  }, [product]);

  // Find matching variant based on selected attributes
  useEffect(() => {
    if (!product?.hasVariants || !product?.variants || product.variants.length === 0) {
      setCurrentVariant(null);
      setAvailableVariants([]);
      return;
    }

    // Find variant that matches all selected attributes
    const matchingVariant = product.variants.find(variant => {
      if (!variant.attributes || variant.attributes.length === 0) return false;
      
      return variant.attributes.every(variantAttr => {
        const selectedValue = selectedAttributes[variantAttr.attributeName];
        return selectedValue === variantAttr.value;
      });
    });

    setCurrentVariant(matchingVariant);
    
    // Find available variants for each attribute
    const available = {};
    product.variants.forEach(variant => {
      if (!variant.isActive) return;
      
      variant.attributes?.forEach(attr => {
        if (!available[attr.attributeName]) {
          available[attr.attributeName] = new Set();
        }
        available[attr.attributeName].add(attr.value);
      });
    });
    
    setAvailableVariants(available);
    
    // Notify parent of variant change
    if (matchingVariant && onVariantChange) {
      onVariantChange(matchingVariant);
    } else if (!matchingVariant && Object.keys(selectedAttributes).length === 0 && onVariantChange) {
      // No variant selected, use base product
      onVariantChange(null);
    }
  }, [selectedAttributes, product, onVariantChange]);

  // Update selected variant when prop changes
  useEffect(() => {
    if (selectedVariant) {
      const attrs = {};
      selectedVariant.attributes?.forEach(attr => {
        attrs[attr.attributeName] = attr.value;
      });
      setSelectedAttributes(attrs);
    }
  }, [selectedVariant]);

  const handleAttributeChange = (attributeName, value) => {
    setSelectedAttributes(prev => ({
      ...prev,
      [attributeName]: value
    }));
  };

  // Get attribute values from product attributes or variants
  const getAttributeValues = (attributeName) => {
    // First try to get from product attributes
    const productAttr = product?.attributes?.find(a => a.attributeName === attributeName);
    if (productAttr?.attributeId) {
      // This is a reference to ProductAttribute model - would need to fetch
      // For now, extract from variants
    }
    
    // Extract unique values from variants
    const values = new Set();
    product?.variants?.forEach(variant => {
      if (!variant.isActive) return;
      variant.attributes?.forEach(attr => {
        if (attr.attributeName === attributeName) {
          values.add(attr.value);
        }
      });
    });
    
    return Array.from(values);
  };

  // Get unique attribute names from variants
  const getAttributeNames = () => {
    if (!product?.hasVariants || !product?.variants) return [];
    
    const names = new Set();
    product.variants.forEach(variant => {
      variant.attributes?.forEach(attr => {
        names.add(attr.attributeName);
      });
    });
    
    return Array.from(names);
  };

  if (!product?.hasVariants || !product?.variants || product.variants.length === 0) {
    return null; // No variants, don't show selector
  }

  const attributeNames = getAttributeNames();
  if (attributeNames.length === 0) {
    return null;
  }

  // Calculate display price - use variant discounted price if available
  const displayPrice = currentVariant?.price !== undefined 
    ? (currentVariant.discountedPrice !== undefined ? currentVariant.discountedPrice : currentVariant.price)
    : (product.discountedPrice || product.price);
  
  // Calculate display stock
  const displayStock = currentVariant?.stock !== undefined 
    ? currentVariant.stock 
    : (product.stock || 0);

  // Get variant image or use product image
  const displayImages = currentVariant?.image 
    ? [currentVariant.image, ...(product.images || [])]
    : (product.images || []);

  return (
    <div className="variant-selector space-y-4">
      {/* Selected Variant Info */}
      {currentVariant && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-800">
            <FaCheck className="text-green-600" />
            <span className="font-semibold">Variant Selected</span>
          </div>
          {currentVariant.sku && (
            <p className="text-sm text-green-700 mt-1">SKU: {currentVariant.sku}</p>
          )}
        </div>
      )}

      {/* Attribute Selectors */}
      {attributeNames.map(attrName => {
        const values = getAttributeValues(attrName);
        const selectedValue = selectedAttributes[attrName];

        return (
          <div key={attrName} className="variant-attribute">
            <label className="block text-sm font-semibold text-gray-700 mb-2 capitalize">
              {attrName === 'Size' ? 'Size' : attrName === 'Color' ? 'Color' : attrName}
              {selectedValue && (
                <span className="ml-2 text-primary font-normal">
                  ({selectedValue})
                </span>
              )}
            </label>
            <div className="flex flex-wrap gap-2">
              {values.map(value => {
                const isSelected = selectedValue === value;
                const isAvailable = availableVariants[attrName]?.has(value) !== false;

                return (
                  <button
                    key={value}
                    onClick={() => isAvailable && handleAttributeChange(attrName, value)}
                    disabled={!isAvailable}
                    className={`
                      px-4 py-2 rounded-lg border-2 transition-all
                      ${isSelected
                        ? 'border-primary bg-primary text-white font-semibold'
                        : isAvailable
                          ? 'border-gray-300 bg-white text-gray-700 hover:border-primary hover:bg-primary-light'
                          : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                      }
                    `}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Variant Price Display */}
      {currentVariant && currentVariant.price !== undefined && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Variant Price:</span>
            <div className="flex items-center gap-2">
              {currentVariant.discount > 0 && currentVariant.discountedPrice !== undefined ? (
                <>
                  <span className="text-xl font-bold text-primary">
                    {currencySymbol}{currentVariant.discountedPrice.toFixed(2)}
                  </span>
                  <span className="text-sm text-gray-500 line-through">
                    {currencySymbol}{currentVariant.price.toLocaleString()}
                  </span>
                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">
                    {currentVariant.discount}% OFF
                  </span>
                </>
              ) : (
                <span className="text-xl font-bold text-primary">
                  {currencySymbol}{currentVariant.price.toLocaleString()}
                </span>
              )}
            </div>
          </div>
          {product.price !== (currentVariant.discountedPrice || currentVariant.price) && (
            <div className="mt-2 text-sm text-gray-500">
              Base price: {currencySymbol}{product.price.toLocaleString()}
            </div>
          )}
        </div>
      )}

      {/* Variant Stock Display */}
      {currentVariant && (
        <div className="text-sm">
          <span className="text-gray-600">Stock:</span>
          <span className={`ml-2 font-semibold ${displayStock > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {displayStock > 0 ? `${displayStock} available` : 'Out of stock'}
          </span>
        </div>
      )}
    </div>
  );
};

export default VariantSelector;
